import {ReactComponent as IconClose} from '#/components/icons/ico-close.svg';
import {DefaultChildren} from '#/utils/utils';
import {motion} from 'framer-motion';
import {FunctionComponent, ReactNode, useRef} from 'react';
import {useClickAway} from 'react-use';

export type ModalProps = {
  title?: ReactNode;
  onClose: () => void;
  hideOnClickAway?: boolean;
  theme?: 'dark' | 'light';
  className?: string;
} & DefaultChildren;

export const Modal: FunctionComponent<ModalProps> = ({
  children,
  title = '',
  theme = 'light',
  hideOnClickAway = true,
  onClose,
  className = '',
}) => {
  const modalWindowRef = useRef<HTMLDivElement>(null);

  const onCloseModal = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.preventDefault();
    e.stopPropagation();
    onClose();
  };

  useClickAway(modalWindowRef, () => {
    if (hideOnClickAway) {
      onClose();
    }
  });

  if (theme === 'dark') {
    return (
      <motion.div
        initial={{opacity: 0}}
        animate={{opacity: 1}}
        exit={{opacity: 0}}
        transition={{ease: 'easeInOut', duration: 0.2}}
        className='absolute flex h-screen w-screen justify-center items-start z-50 bg-black/[.5] md:p-8 sm:pt-24 md:pt-24'
      >
        <motion.div
          ref={modalWindowRef}
          initial={{opacity: 0, scale: 0.75}}
          animate={{opacity: 1, scale: 1}}
          exit={{opacity: 0, scale: 0}}
          transition={{ease: 'easeInOut', duration: 0.2}}
          className={
            'w-full h-full sm:w-auto sm:h-auto md:h-auto flex flex-col max-h-full justify-start bg-grayblue rounded-2xl overflow-hidden z-50 shadow-2xl'
          }
        >
          {title && (
            <div className='w-full bg-transparent px-6 pt-5 rounded-t'>
              <div className='flex justify-between border-b border-gray-400 pb-3'>
                <span className='text-white font-bold'>{title}</span>
                <button className='ml-6 mr-0' onClick={onCloseModal}>
                  <IconClose className='hover:fill-blue-600' />
                </button>
              </div>
            </div>
          )}

          {children}
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{opacity: 0}}
      animate={{opacity: 1}}
      exit={{opacity: 0}}
      transition={{ease: 'easeInOut', duration: 0.2}}
      className='absolute flex h-screen w-screen justify-center items-start z-50 bg-black/[.5] md:p-8 sm:pt-12 md:pt-24'
    >
      <motion.div
        ref={modalWindowRef}
        initial={{opacity: 0, scale: 0.75}}
        animate={{opacity: 1, scale: 1}}
        exit={{opacity: 0, scale: 0}}
        transition={{ease: 'easeInOut', duration: 0.2}}
        className={`w-full h-full sm:w-auto sm:h-auto flex flex-col max-h-full overflow-hidden justify-start bg-white border-none ${className}`}
      >
        {title && (
          <div className='bg-black pl-6 py-4 flex justify-between rounded-t'>
            <span className='text-white font-bold'>{title}</span>
            <button className='ml-6 mr-4' onClick={onCloseModal}>
              <IconClose className='hover:fill-blue-600' />
            </button>
          </div>
        )}

        {children}
      </motion.div>
    </motion.div>
  );
};
